import React, { useState } from 'react';
import {
  getIconForCategory,
  getImageUrl,
  formatPrice,
  isChassisProduct
} from './AppUtils.js';
import { isAdminMode } from './constants.js';

// ============================================================================
// WARNING MODAL COMPONENT
// ============================================================================

const WarningModal = ({ message, productName, onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-[20px] w-full max-w-sm flex flex-col shadow-2xl">
      <div className="p-4 border-b">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-3 bg-red-100 rounded-full flex items-center justify-center">
            <span className="text-red-600 text-2xl font-bold">!</span>
          </div>
          <h3 className="font-semibold text-lg mb-1">Compatibility Warning</h3>
          <div className="text-sm text-gray-600">
            <strong>{productName}</strong> cannot be selected
          </div>
        </div>
      </div>
      
      <div className="p-4">
        <div className="text-sm text-gray-700 leading-relaxed">
          {message}
        </div>
      </div>
      
      <div className="p-4 border-t">
        <button 
          onClick={onClose}
          className="w-full bg-gray-600 hover:bg-gray-700 text-white py-3 rounded-[15px] font-medium transition-colors"
        >
          Got It
        </button>
      </div>
    </div>
  </div>
);

// ============================================================================
// SEAMLESS PRODUCT CARD COMPONENT - CLS OPTIMIZED WITH FIXED DIMENSIONS
// ============================================================================

const ProductCard = ({ 
  product, 
  index, 
  isSelected, 
  isInBuild, 
  compatibilityStatus,
  onSelect, 
  onToggle,
  onInfo,
  mainCategory,
  subCategory,
  productInBuild,
  categoryConfig = {}
}) => {
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const isLocked = isChassisProduct(product, mainCategory, subCategory);

  return (
    <>
      {/* CSS for seamless card styling - FIXED positioning & CLS prevention */}
      <style>{`
        .product-card-container {
          position: relative;
          width: 164px !important;
          height: 228px !important;
          padding: 0px;
          border-radius: 20px;
          transform-origin: center;
          transition: transform 0.2s ease-out;
          border: none !important;
          flex-shrink: 0;
          contain: layout style;
        }

        
        .product-card-inner {
          width: 164px;
          height: 228px;
          padding: 8px;
          border-radius: 15px;
          position: relative;
          z-index: 1;
          transform-origin: center;
          display: flex;
          flex-direction: column;
          gap: 8px;
          border: none !important;
          outline: none !important;
          box-shadow: none;
          transition: all 0.2s ease-out;
        }
        
        .has-animated-border .product-card-inner {
          background: linear-gradient(135deg, #FECF00, #e6bb00);
          background-size: 200% 200%;
          animation: none;
          border: none !important;
          box-shadow: 0 4px 12px rgba(254, 207, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3);
        }
        
        @keyframes shimmer {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        .no-animated-border .product-card-inner {
          background: linear-gradient(135deg, #e8e8e8, #dcdcdc);
          border: none !important;
          outline: none !important;
          transition: all 0.2s ease-out;
        }
        
        
        .no-animated-border.selected .product-card-inner {
          background: linear-gradient(135deg, #f9fafb, #ffffff) !important;
          border: none !important;
          outline: none !important;
          box-shadow: 0 4px 12px -2px rgba(0, 0, 0, 0.15) !important;
        }

        /* FIXED: Consistent positioning with explicit dimensions for CLS prevention */
        .product-image-fixed {
          width: 148px;
          height: 114px;
          flex-shrink: 0;
          border-radius: 8px;
          position: relative;
          overflow: hidden;
          background: linear-gradient(135deg, #e4e4e7 0%, #d4d4d8 100%);
        }
        
        .product-details-fixed {
          height: 68px;
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
        }
        
        .product-name-fixed {
          height: 48px;
          line-height: 1.1;
          font-size: 11px;
          font-weight: 400;
          margin-bottom: 4px;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
        }

        .product-price-fixed {
          font-size: 11px;
          font-weight: 400;
          margin-top: auto;
          line-height: 1;
          height: 16px;
          display: flex;
          align-items: center;
        }

        /* Button positioning - ALWAYS same position */
        .product-buttons-fixed {
          position: absolute;
          bottom: 8px;
          right: 8px;
          display: flex;
          gap: 2px;
          z-index: 2;
        }
        
        .product-button {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease-out;
          flex-shrink: 0;
        }
        
        .add-button {
          background: rgba(0, 0, 0, 0.8);
          color: white;
        }
        
        .add-button:hover {
          background: rgba(0, 0, 0, 0.9);
          transform: scale(1.05);
        }
        
        .check-button {
          background: rgba(34, 197, 94, 0.9);
          color: white;
        }
        
        .info-button {
          background: transparent;
          color: white;
        }
        
        .info-button:hover {
          background: rgba(0, 0, 0, 0.2);
          transform: scale(1.05);
        }

        /* Image loading skeleton for CLS prevention */
        .product-image-skeleton {
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, #e4e4e7 25%, #f4f4f5 50%, #e4e4e7 75%);
          background-size: 200% 100%;
          animation: skeleton-shimmer 1.5s infinite;
        }

        @keyframes skeleton-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

      {/* FIXED: Clean wrapper with explicit dimensions */}
      <div 
        className="flex-shrink-0" 
        style={{ 
          scrollSnapAlign: 'start',
          width: '164px',
          height: '228px',
          contain: 'layout style'
        }}
      >
        <div
          onClick={() => onSelect(index)}
          className={`product-card-container cursor-pointer overflow-visible transition-transform duration-200 ${
            isInBuild ? 'has-animated-border z-10' : 'no-animated-border'
          } ${isSelected ? 'selected scale-[1.05] shadow-lg z-20' : 'shadow-lg'} ${
            isLocked && !isInBuild ? 'opacity-40 grayscale' : ''
          }`}
        >
          <div className="product-card-inner relative">
            
            {/* Product Image - CLS OPTIMIZED with skeleton loader */}
            <div className="product-image-fixed">
              {/* Skeleton placeholder - prevents CLS */}
              {!imageLoaded && (
                <div className="product-image-skeleton" />
              )}
              
              {(() => {
                const imageUrl = getImageUrl(product.image);
                return imageUrl ? (
                  <img 
                    src={imageUrl}
                    alt={product.name}
                    loading="lazy"
                    decoding="async"
                    width="148"
                    height="114"
                    className={`w-full h-full object-cover transition-opacity duration-200 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                    onLoad={() => setImageLoaded(true)}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.querySelector('.fallback-icon').style.display = 'flex';
                      setImageLoaded(true);
                    }}
                  />
                ) : null;
              })()}
              <div 
                className="fallback-icon absolute inset-0 items-center justify-center text-zinc-500 text-xl bg-gradient-to-br from-zinc-200 to-zinc-300" 
                style={{ display: getImageUrl(product.image) ? 'none' : 'flex' }}
              >
                {getIconForCategory(subCategory, categoryConfig, mainCategory)}
              </div>
              
              {/* Warning Icon - FIXED position */}
              {compatibilityStatus.showWarning && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowWarningModal(true);
                  }}
                  className="absolute top-2 right-2 w-6 h-6 bg-yellow-500 text-white rounded-full flex items-center justify-center text-xs font-bold hover:bg-yellow-600 transition-colors shadow-lg animate-pulse"
                  title="Click to see compatibility warning"
                >
                  !
                </button>
              )}
            </div>
            
            {/* Product Details - FIXED height and positioning */}
            <div className="product-details-fixed">
              <div 
                className={`product-name-fixed ${
                  isInBuild ? 'text-gray-800' : 'text-black'
                }`}
                title={product.name}
              >
                {product.name}
              </div>
              
              <div className="product-price-fixed text-black">
                {formatPrice(product.price)}
                {isAdminMode() && product.testPrice !== null && product.testPrice !== undefined && (
                  <span className="ml-1 text-blue-600" title="Test Price">
                    ({formatPrice(product.testPrice)})
                  </span>
                )}
              </div>
            </div>
            
            {/* Action Buttons - ALWAYS same position */}
            <div className="product-buttons-fixed">
              {/* Info Button */}
              {product.description && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onInfo(product);
                  }}
                  className="product-button info-button"
                  title="View details"
                >
                  <img 
                    src="/images/icons/info-24.png" 
                    alt="Info" 
                    loading="lazy"
                    width="20"
                    height="20"
                    className="w-5 h-5"
                    onError={(e) => {
                      e.target.src = './images/icons/info-24.png';
                      e.target.onerror = () => {
                        e.target.style.display = 'none';
                        e.target.parentElement.insertAdjacentHTML('beforeend', '<span class="text-white text-[10px] font-bold bg-black rounded-full w-5 h-5 flex items-center justify-center">i</span>');
                      };
                    }}
                  />
                </button>
              )}
              
              {/* Add/Remove Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle(product, mainCategory, subCategory);
                }}
                className={`product-button ${
                  (isLocked && !isInBuild) ? 'cursor-not-allowed opacity-50' : ''
                }`}
                title={isInBuild ? 'Remove from build' : 'Add to build'}
              >
                {isInBuild ? (
                  <span className="text-white text-[10px] font-bold bg-black rounded-full w-5 h-5 flex items-center justify-center">✓</span>
                ) : (
                  <img 
                    src="/images/icons/plus-24.png" 
                    alt="Add" 
                    loading="lazy"
                    width="20"
                    height="20"
                    className="w-5 h-5"
                    onError={(e) => {
                      e.target.src = './images/icons/plus-24.png';
                      e.target.onerror = () => {
                        e.target.style.display = 'none';
                        e.target.parentElement.insertAdjacentHTML('beforeend', '<span class="text-white text-[10px] font-bold bg-black rounded-full w-5 h-5 flex items-center justify-center">+</span>');
                      };
                    }}
                  />
                )}
              </button>
            </div>
            
          </div>
        </div>
      </div>

      {/* Warning Modal - Shows when warning icon is clicked */}
      {showWarningModal && compatibilityStatus.showWarning && (
        <WarningModal 
          message={compatibilityStatus.warningMessage}
          productName={product.name}
          onClose={() => setShowWarningModal(false)}
        />
      )}
    </>
  );
};

export default ProductCard;